import { Separator as BaseSeparator } from '@base-ui/react';
import { type Theme } from '@emotion/react';
import styled from '@emotion/styled';
import type { FC } from 'react';

type SeparatorSpacing = 'regular' | 'spacious';
type SeparatorTone = 'default' | 'subtle' | 'border' | 'borderSubtle';

export interface SeparatorProps extends BaseSeparator.Props {
  spacing?: SeparatorSpacing;
  tone?: SeparatorTone;
}

const toneBackground = (theme: Theme, tone?: SeparatorTone) => {
  if (tone === 'subtle') return theme.colors.white[12];
  if (tone === 'border') return theme.colors.border.default;
  if (tone === 'borderSubtle') return theme.colors.border.subtle;

  return theme.colors.white[20];
};

const StyledSeparator = styled(BaseSeparator, {
  shouldForwardProp: (prop) => prop !== 'spacing' && prop !== 'tone',
})<{ spacing?: SeparatorSpacing; tone?: SeparatorTone }>(({
  theme,
  orientation,
  spacing,
  tone,
}) => {
  const isVertical = orientation === 'vertical';

  return {
    background: toneBackground(theme, tone),
    alignSelf: 'stretch',
    ...(isVertical ? { width: 1 } : { height: 1 }),
    ...(spacing === 'regular' && {
      margin: isVertical ? '0 2px' : '4px 0',
    }),
    ...(spacing === 'spacious' && {
      margin: isVertical ? '0 7.5px' : '7.5px 0',
    }),
  };
});

const Separator: FC<SeparatorProps> = ({ orientation = 'horizontal', spacing, tone, ...rest }) => (
  <StyledSeparator orientation={orientation} spacing={spacing} tone={tone} {...rest} />
);

Separator.displayName = 'Separator';

export default Separator;
